import { ROUTE_PURPOSES_KEYS } from "@config/constants";

export type Country = {
  name: string;
  code: string; // ISO 3166-1 alpha-2
};

export type LocationSuggestion = {
  id: string;
  name: string; // e.g. "Barsha Heights"
  displayName: string; // e.g. "Barsha Heights, Dubai, UAE"
  city?: string;
  state?: string;
  country?: string;
  countryCode?: string;
  lat: number;
  lon: number;
};

export type Day =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

// CARS
export type CarType = "sedan" | "suv" | "hatchback";
export type Car = {
  id: string;
  userId?: string; // FK -> User.id
  make: string; // Toyota, Honda...
  model: string; // Corolla, Civic...
  type: CarType;
  plateNumber: string; // Vehicle registration plate number
  color?: string | null;
  year?: number | null;
  createdAt?: string;
};

// COMMUTES
export type Commute = {
  id: string;
  userId?: string;
  ownerName?: string | null; // Full name of the commute owner (driver/rider)
  ownerAvatarUrl?: string | null; // Avatar URL of the commute owner (from user_public_profiles)
  fromPlace: LocationSuggestion; // "Barsha Heights"
  toPlace: LocationSuggestion; // "Dubai Marina"
  leavesAt: string; // "08:00"
  returnsAt: string; // "17:30"
  days: Day[]; // ["mon", "tue", "wed", "thu", "fri", "sat", "sun"]
  date: string | null; // "10-01-2026"
  createdAt: string;
  type: UserRole; // "driver" | "rider"
  purpose: RoutePurpose; // Purpose of the trip, drives the marker emoji
  purposeDetail?: string | null; // Optional free-text detail, max 25 chars
  isActive?: boolean; // Whether the commute is active (paused/resumed)
  /** True for one-time routes whose scheduled time (date + leavesAt) has passed. Computed client-side. */
  isExpired?: boolean;
  // only for drivers:
  pricePerSeat?: number | null; // in USD
  availableSeats?: number | null;
  vehicle?: Car | null;
};

export type UserRole = "driver" | "rider";

/** Derived from ROUTE_PURPOSES_KEYS so the two can never drift apart. */
export type RoutePurpose = (typeof ROUTE_PURPOSES_KEYS)[number];

export type User = {
  id: string;
  fullName: string;
  email: string;
  createdAt: string;
};

/** Active row from `credit_products` (subset used in billing UI) */
export type CreditProduct = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  credits: number;
  bonus_credits: number;
  total_credits: number;
  /** Smallest currency unit (e.g. 7000 = $70.00). */
  price_amount: number;
  currency: string;
  is_recommended: boolean;
  sort_order: number;
  apple_product_id?: string | null;
  google_product_id?: string | null;
};

// export type SubscriptionPlan = "free_trial" | "basic" | "pro" | "max";
// export type BillingPeriod = "monthly" | "yearly";

// export type Subscription = {
//   id: string;
//   userId: string;
//   plan: SubscriptionPlan;
//   period: BillingPeriod | null; // null during trial
//   isActive: boolean;
//   startedAt: string;
//   expiresAt: string;
// };
// export type Credits = {
//   userId: string; // PK = userId (1 row per user)
//   balance: number; // default 30
//   lastUpdatedAt: string;
// };
// COMMUTE REQUESTS
export type CommuteRequestStatus =
  | "pending"
  | "accepted"
  | "rejected"
  | "cancelled";

export type CommuteRequest = {
  id: string;
  commuteId: string;
  requesterId: string;
  receiverId: string;
  driverId: string;
  status: CommuteRequestStatus;
  createdAt: string;
  updatedAt: string;
  message?: string | null;
  // Denormalized commute data for display
  commute?: Commute;
  // User names for display
  requesterName?: string | null;
  receiverName?: string | null;
  // Contact information (only available when request is accepted)
  requesterPhone?: string | null;
  requesterPhoneCountryCode?: string | null;
  receiverPhone?: string | null;
  receiverPhoneCountryCode?: string | null;
};

export type RequestDetailViewerRole = "requester" | "receiver";

export type RequestDetail = {
  id: string;
  status: CommuteRequestStatus;
  createdAt: string;
  updatedAt: string;
  message?: string | null;
  viewerRole: RequestDetailViewerRole;
  otherUser: {
    id: string;
    name: string | null;
    avatar: string | null;
  };
  commute?: Commute;
};

// FEEDBACK
export type UserFeedback = {
  id: string;
  requestId: string;
  rating: number;
  review: string | null;
  createdAt: string;
  raterId: string;
  raterFullName: string | null;
  raterAvatarUrl: string | null;
  revieweeId?: string;
  revieweeFullName?: string | null;
  revieweeAvatarUrl?: string | null;
  fromAddress: string;
  toAddress: string;
};

export type UserFeedbackSummary = {
  userId: string;
  totalReviews: number;
  averageRating: number;
  rating1Count: number;
  rating2Count: number;
  rating3Count: number;
  rating4Count: number;
  rating5Count: number;
  latestReviewAt: string | null;
  updatedAt: string;
};

// NOTIFICATIONS
export type NotificationType =
  | "request_sent"
  | "request_accepted"
  | "request_rejected"
  | "request_cancelled"
  | "new_matching_commute"
  | "system_announcement"
  | "credits_purchased"
  | "credits_granted"
  | "credits_refunded";

export type AppNotification = {
  id: string;
  userId: string;
  type: NotificationType;
  title: string;
  body: string;
  entityId: string | null;
  entityType: string | null;
  data: Record<string, unknown>;
  isRead: boolean;
  createdAt: string;
  url?: string;
};

// BILLING

export type Credits = {
  userId: string;
  balance: number;
  updatedAt: string;
};

export type CreditTransaction = {
  id: string;
  userId: string;
  type: CreditTransactionType;
  reason: CreditTransactionReason;
  amount: number;
  balanceAfter: number;
  metadata?: Record<string, unknown>;
  createdAt: string;
};

// Broad transaction categories
export const CREDIT_TRANSACTION_TYPES = {
  GRANT: "grant" as const,
  PURCHASE: "purchase" as const,
  UNLOCK: "unlock" as const,
  REFUND: "refund" as const,
  ADJUSTMENT: "adjustment" as const,
};

export type CreditTransactionType =
  (typeof CREDIT_TRANSACTION_TYPES)[keyof typeof CREDIT_TRANSACTION_TYPES];

// Specific reasons
export const CREDIT_TRANSACTION_REASONS = {
  SIGNUP_BONUS: "signup_bonus" as const,
  UNLOCK_CONTACT: "unlock_contact" as const,
  BUNDLE_PURCHASE_STARTER: "bundle_purchase_starter" as const,
  BUNDLE_PURCHASE_STANDARD: "bundle_purchase_standard" as const,
  BUNDLE_PURCHASE_PRO: "bundle_purchase_pro" as const,
  MANUAL_ADJUSTMENT: "manual_adjustment" as const,
  REFUND: "refund" as const,
};

export type CreditTransactionReason =
  (typeof CREDIT_TRANSACTION_REASONS)[keyof typeof CREDIT_TRANSACTION_REASONS];

export type BillingProduct = {
  id: "starter" | "standard" | "pro";
  priceUsd: number;
  credits: number;
};

export type BillingPurchase = {
  id: string;
  userId: string;
  productId: string;
  amountUsd: number;
  provider: "paddle";
  providerTxId: string;
  status: "paid" | "refunded";
  createdAt: string;
};
